import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ExternalLink, Quote, Star } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { INFO, STORIA_IMAGES } from "@/data/content";

const STATS = [
  { value: INFO.rating, label: "Votazione Google" },
  { value: `${INFO.reviewCount}`, label: "Recensioni" },
  { value: "1–10 €", label: "Spesa a persona" },
  { value: "6:30–00", label: "Aperti tutti i giorni*" },
];

export const Storia = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="storia" ref={ref} className="scroll-mt-20 bg-espresso py-20 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <div>
          <Reveal>
            <Eyebrow>Chi Siamo</Eyebrow>
            <h2 className="mt-4 text-balance font-serif text-3xl font-semibold leading-tight tracking-tight text-cream sm:text-4xl lg:text-5xl">
              Il bar dove la giornata si{" "}
              <span className="italic text-terra-light">incontra</span>.
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-7 text-base leading-relaxed text-cream/70 sm:text-lg">
              Un locale rinnovato, con ampi spazi interni ed esterni: dentro
              il banco profuma di caffè, fuori le tavole aspettano l'ora
              dell'aperitivo. Al Bar Incontro la giornata comincia presto —
              alle 6:30 — e finisce tardi.
            </p>
            <p className="mt-4 text-base leading-relaxed text-cream/70 sm:text-lg">
              Brioche fresche, una vetrina ricca di dolci, personale
              gentile e cura per ogni dettaglio: pulizia, comodità,
              parcheggio facile. Per questo i clienti tornano, ogni giorno.
            </p>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="mt-10 grid grid-cols-2 gap-6 sm:grid-cols-4">
              {STATS.map((s) => (
                <div key={s.label} className="border-t border-sand/20 pt-4">
                  <p className="font-serif text-2xl font-semibold text-cream sm:text-3xl">
                    {s.value}
                  </p>
                  <p className="mt-1 text-xs text-cream/55">{s.label}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-cream/40">
              *Domenica dalle 7:30 · Martedì chiuso
            </p>
          </Reveal>

          <Reveal delay={0.24}>
            <figure className="mt-10 rounded-2xl border border-sand/15 bg-mocha/60 p-7">
              <Quote className="size-6 text-terra" />
              <blockquote className="mt-3 font-serif text-xl italic leading-snug text-cream/90">
                Ottimo locale, nonché servizio e cortesia dei gestori.
              </blockquote>
              <figcaption className="mt-4 flex items-center gap-2 text-sm text-cream/55">
                <span className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="size-3.5 fill-gold text-gold" />
                  ))}
                </span>
                Graziella Lippolis · Local Guide
              </figcaption>
            </figure>
          </Reveal>
        </div>

        <div className="relative">
          <Reveal className="relative overflow-hidden rounded-2xl">
            <a
              href="https://restaurantguru.it/Bar-Incontro-Pesaro"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="storia-image-link-interior"
              className="group/photolink block"
            >
              <motion.img
                style={{ y: yImg }}
                src={STORIA_IMAGES.interior}
                alt="Il banco e la sala del Bar Incontro"
                className="h-[420px] w-full scale-110 object-cover lg:h-[560px]"
                loading="lazy"
              />
              <span className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-espresso/70 px-3 py-1.5 text-[11px] text-cream/80 opacity-0 backdrop-blur transition-opacity duration-300 group-hover/photolink:opacity-100">
                Altre foto
                <ExternalLink className="size-3" />
              </span>
            </a>
            <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-sand/20" />
          </Reveal>

          <Reveal
            delay={0.15}
            className="absolute -bottom-8 -left-2 w-48 overflow-hidden rounded-xl shadow-2xl shadow-black/50 sm:-left-6 sm:w-64"
          >
            <a
              href="https://www.facebook.com/p/Bar-Incontro-100054482108844/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="storia-image-link-facebook"
              className="group/photolink block"
            >
              <img
                src={STORIA_IMAGES.breakfast}
                alt="Il dehors del Bar Incontro"
                className="h-40 w-full object-cover sm:h-52"
                loading="lazy"
              />
              <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-espresso/70 px-3 py-1.5 text-[11px] text-cream/80 opacity-0 backdrop-blur transition-opacity duration-300 group-hover/photolink:opacity-100">
                Facebook
                <ExternalLink className="size-3" />
              </span>
            </a>
            <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-cream/20" />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
